<template>
    <div>
        <transition name="fade">
            <Row v-if="mainTable.visible" :gutter="16">
                <Col span="24">
                    <search-card
                        :form="mainTable.searchForm"
                        :table-load-event="handleSearch"
                        :table-search="mainTable.search"
                        :colSpan="4"
                        :searchBtnColSpan="4"
                    />
                </Col>
            </Row>
        </transition>

        <Card>
            <!-- 工具栏 -->
            <Row :gutter="16" class="tool-bar-row">
                <Col span="24">
                    <div class="global-tool-bar">
                        <div class="left-bar">
                            <span class="title">定金单定金统计</span>
                            <div class="filter-controls">
                                <span class="filter-label">统计时间:</span>
                                <RadioGroup v-model="timeMode" size="small" @on-change="handleTimeModeChange">
                                    <Radio label="range">时间范围</Radio>
                                    <Radio label="deadline">时间截止</Radio>
                                </RadioGroup>

                                <!-- 时间范围选择器 -->
                                <DatePicker
                                    v-if="timeMode === 'range'"
                                    type="daterange"
                                    v-model="dateRange"
                                    size="small"
                                    transfer
                                    :clearable="false"
                                    placement="bottom-end"
                                    placeholder="选择期初和期末时间"
                                    class="date-picker"
                                    @on-change="handleDateChange"
                                />

                                <!-- 时间截止选择器 -->
                                <DatePicker
                                    v-if="timeMode === 'deadline'"
                                    type="date"
                                    v-model="endDate"
                                    size="small"
                                    transfer
                                    :clearable="false"
                                    placement="bottom-end"
                                    placeholder="选择截止时间"
                                    class="date-picker"
                                    @on-change="handleEndDateChange"
                                />
                            </div>
                        </div>
                        <div class="right-bar">
                            <Button
                                type="info"
                                size="small"
                                class="action-btn"
                                icon="ios-send"
                                :loading="exportLoading"
                                @click="handleExport"
                            >
                                导出
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                class="action-btn"
                                icon="ios-search"
                                @click="toggleSearch"
                            >
                                搜索
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            <div class="formula-legend-container">
                <div class="legend-item">
                    <div class="tag income">收定金</div>
                    <span class="equals">=</span>
                    <span class="formula-text">定金已支付的定金单定金 + 全额支付的定金单定金</span>
                </div>
                <div class="vertical-divider"></div>
                <div class="legend-item">
                    <div class="tag expense">退定金</div>
                    <span class="equals">=</span>
                    <span class="formula-text">撤销 + 关闭 + 已发货定金单 + 部分发货的定金单定金 + 违约</span>
                </div>
            </div>

            <!-- 数据表格 -->
            <Row :gutter="16">
                <Col span="24">
                    <stand-pagination-table
                        :columns="mainTable.columns"
                        :data="mainTable.data"
                        :loading="mainTable.loading"
                        :table-search="mainTable.search"
                        :table-load-event="handleSearch"
                        :autoHeight="true"
                        summaryLabelField = "depositBillRecordCode"
                        :summaryFields="['inflow', 'consume']"
                        :totalSummaryFieldDatas="mainTable.sumData"
                        :showPage="true"
                    />
                </Col>
            </Row>
        </Card>

    </div>
</template>

<script>
import SearchCard from "@/components/searchCard/searchCard.vue";
import search from "@/libs/search";
import StandPaginationTable from "@/components/pagination-table/pagination-table.vue";
import { number2ThousandsFormat } from "@/components/number-input/display-format";
import { getThisMonthStart, getTodayDateString } from "@/libs/logic";
import { getSeedFarmDepositStatSqlDetail } from "@/api/modules/sale-management/sale-stat";
import { DOWNLOAD_SERVER } from "@/api/modules/files";

// 日期时间渲染函数
const dateTimeRender = (h, dateTime) => {
    if (!dateTime) return h('span', '-');
    return h('span', dateTime);
};

// 发货单状态映射
const DELIVERY_STATUS_MAP = {
    0: '未发货',
    1: '已发货',
    2: '关闭',
    3: '撤销'
};

// 表格列配置常量
const TABLE_COLUMNS = Object.freeze([
    {
        title: "定金单编号",
        key: "depositBillRecordCode",
        width: 150,
        align: "center",
        excel: true
    },
    {
        title: "单据编号",
        key: "relateCode",
        width: 100,
        align: "center",
        excel: true
    },
    {
        title: "苗场编号",
        key: "seedFarmCode",
        width: 60,
        align: "center",
        excel: true
    },
    {
        title: "苗场名称",
        key: "seedFarmName",
        width: 100,
        align: "center",
        excel: true
    },
    {
        title: "客户账号",
        key: "userName",
        width: 80,
        align: "center",
        excel: true
    },
    {
        title: "客户名称",
        key: "userPersonName",
        width: 60,
        align: "center",
        excel: true
    },
    {
        title: "收定金",
        key: "inflow",
        width: 80,
        align: "center",
        className: "money-column",
        excel: true
    },
    {
        title: "退定金",
        key: "consume",
        width: 80,
        align: "center",
        className: "money-column",
        excel: true
    },
    {
        title: "支付状态",
        key: "depositBillRecordPaidStatus",
        width: 80,
        align: "center",
        excel: true
    },
    {
        title: "定单状态",
        key: "depositBillRecordOrderStatus",
        width: 80,
        align: "center",
        excel: true
    },
    {
        title: "发货单状态",
        key: "preSaleDeliveryRecordStatus",
        width: 80,
        align: "center",
        render: (h, params) => {
            const value = params.row.preSaleDeliveryRecordStatus;
            const text = DELIVERY_STATUS_MAP[value] || '-';
            return h('span', text);
        },
        excel: true
    },
    {
        title: "操作时间",
        key: "cTime",
        width: 130,
        align: "center",
        render: (h, params) => dateTimeRender(h, params.row.cTime),
        excel: true
    }
]);


export default {
    name: "deposit-deposit-stat-list",
    components: {
        StandPaginationTable,
        SearchCard
    },
    data() {
        return {
            // 日期范围
            dateRange: [],
            // 截止日期
            endDate: '',
            // 时间模式：'range' 时间范围, 'deadline' 时间截止
            timeMode: 'range',
            // 导出状态
            exportLoading: false,
            // 表格配置
            mainTable: {
                columns: this.createEnhancedColumns(),
                data: [],
                sumData: [],
                search: search.getSearchParams(),
                loading: false,
                visible: false,
                searchForm: [
                    {
                        label: "苗场",
                        type: "Input",
                        value: "",
                        name: "seedFarmCode",
                        sql: "eq",
                        custom: true
                    }
                ]
            }
        };
    },
    created() {
        this.initializePage();
    },
    methods: {
        // 引入工具函数
        getTodayDateString,
        getThisMonthStart,

        // ==================== 初始化相关 ====================

        /**
         * 初始化页面
         */
        initializePage() {
            this.setDefaultDateRange();
            this.loadTableData();
        },

        /**
         * 设置默认日期范围
         */
        setDefaultDateRange() {
            if (this.timeMode === 'range') {
                const beginDate = this.getThisMonthStart();
                const endDate = this.getTodayDateString();
                this.dateRange = [beginDate, endDate];
            }
            else {
                this.endDate = this.getTodayDateString();
            }
        },

        /**
         * 处理时间模式变化
         */
        handleTimeModeChange() {
            this.setDefaultDateRange();
            this.loadTableData();
        },

        /**
         * 处理截止日期变化
         */
        handleEndDateChange() {
            this.loadTableData();
        },

        // ==================== 表格配置相关 ====================

        /**
         * 创建增强版表格列配置
         */
        createEnhancedColumns() {
            return TABLE_COLUMNS.map(column => {
                if (column.className === "money-column") {
                    return {
                        ...column,
                        render: (h, params) => this.renderMoneyColumn(h, params.row[column.key])
                    };
                }
                return column;
            });
        },

        /**
         * 金额列渲染函数
         */
        renderMoneyColumn(h, value) {
            const numValue = value || 0;
            const valueClass = numValue < 0 ? "money-negative" : numValue > 0 ? "money-positive" : "";

            return h("div", {
                attrs: {
                    class: "money-cell"
                }
            }, [
                h("span", {
                    attrs: {
                        class: "money-text " + valueClass
                    }
                }, number2ThousandsFormat(numValue, 4, "￥"))
            ]);
        },

        // ==================== 事件处理相关 ====================

        /**
         * 处理搜索
         */
        handleSearch(reset = false) {
            if (reset) {
                search.globalResetSearch(this.mainTable.search);
            }

            this.updateSearchParams();
            this.loadTableData();
        },

        /**
         * 处理日期变化
         */
        handleDateChange() {
            this.loadTableData();
        },

        /**
         * 切换搜索显示
         */
        toggleSearch() {
            this.mainTable.visible = !this.mainTable.visible;
        },

        /**
         * 处理导出
         */
        async handleExport() {
            const exportSearch = this.prepareExportSearch();
            console.log(exportSearch);
            this.mainTable.loading = true;
            getSeedFarmDepositStatSqlDetail(exportSearch)
                .then(this.handleExportResponse)
                .catch(this.handleExportError)
                .finally(() => {
                    this.mainTable.loading = false;
                });
        },
        prepareExportSearch() {
            const exportSearch = search.deepClone(this.mainTable.search);
            exportSearch.limit.enable = false;
            exportSearch.excelExport.enable = true;
            exportSearch.excelExport.columns2 = this.mainTable.columns
                .filter((col) => col.excel)
                .map((col) => ({
                    key: col.key,
                    title: col.title
                }));

            return exportSearch;
        },
        handleExportResponse(res) {
            if (res.code === 0 && res.data.exportFileName2) {
                const url = DOWNLOAD_SERVER + res.data.exportFileName2;
                this.$downLoadFile(url);
                this.$Message.success('导出成功');
            }
            else {
                this.$Message.error(res.message || '导出失败');
            }
        },
        handleExportError(error) {
            console.error('导出定金明细列表失败:', error);
            this.$Message.error('导出失败，请稍后重试');
        },

        // ==================== 数据处理相关 ====================

        /**
         * 更新搜索参数
         */
        updateSearchParams() {
            // 基础配置
            this.mainTable.search.needExport = 0;

            // 设置日期过滤
            this.setDateRangeFilter();
        },

        /**
         * 设置日期过滤条件
         */
        setDateRangeFilter() {
            this.mainTable.search.filters.enable = true;
            search.globalRemoveFilterConditionKey(this.mainTable.search, "beginDate");
            search.globalRemoveFilterConditionKey(this.mainTable.search, "endDate");
            if (this.timeMode === 'range') {
                // 时间范围模式：设置开始和结束日期
                if (!this.dateRange || this.dateRange.length !== 2) {
                    console.warn("日期范围不完整，请选择开始和结束日期");
                    return;
                }

                // 添加日期过滤条件
                search.globalAddFilterConditionKey(
                    this.mainTable.search,
                    "beginDate",
                    "eq",
                    this.dateRange[0],
                    true
                );

                search.globalAddFilterConditionKey(
                    this.mainTable.search,
                    "endDate",
                    "eq",
                    this.dateRange[1],
                    true
                );
            }
            else {
                // 时间截止模式：只设置结束日期
                if (!this.endDate) {
                    console.warn("请选择截止日期");
                    return;
                }

                search.globalAddFilterConditionKey(
                    this.mainTable.search,
                    "endDate",
                    "eq",
                    this.endDate,
                    true
                );
            }
        },

        /**
         * 加载表格数据
         */
        async loadTableData(reset = false) {
            // 防重复请求检查
            if (this.mainTable.loading) {
                return;
            }

            // 参数验证
            if (!this.validateSearchParams()) {
                return;
            }

            this.mainTable.loading = true;
            if (reset) {
                search.globalResetSearch(this.mainTable.tableSearch);
            }
            try {
                this.updateSearchParams();
                const response = await getSeedFarmDepositStatSqlDetail(this.mainTable.search);
                this.handleDataResponse(response);
            }
            catch (error) {
                this.handleDataError(error);
            }
            finally {
                this.mainTable.loading = false;
            }
        },

        /**
         * 验证搜索参数
         */
        validateSearchParams() {
            if (this.timeMode === 'range') {
                // 验证日期范围
                if (!this.dateRange || this.dateRange.length !== 2) {
                    this.$Message.warning("请选择完整的日期范围");
                    return false;
                }

                // 验证日期格式
                const [startDate, endDate] = this.dateRange;
                if (!startDate || !endDate) {
                    this.$Message.warning("日期格式不正确");
                    return false;
                }

                // 验证日期逻辑
                if (new Date(startDate) > new Date(endDate)) {
                    this.$Message.warning("开始日期不能晚于结束日期");
                    return false;
                }
            }
            else {
                // 验证截止日期
                if (!this.endDate) {
                    this.$Message.warning("请选择截止日期");
                    return false;
                }
            }

            return true;
        },

        /**
         * 处理数据响应
         */
        handleDataResponse(response) {
            if (!response) {
                this.$Message.error("服务器响应异常");
                return;
            }

            if (response.code === 0) {
                const list = (response.data && response.data.list) || [];
                const sumData = (response.data && response.data.sum) || {};
                search.globalSetFilterRecordCount(this.mainTable.search, response.data.filterRecordCount);
                // 数据验证
                if (!Array.isArray(list)) {
                    console.warn("服务器返回的数据格式异常:", response.data);
                    this.mainTable.data = [];
                    return;
                }

                this.mainTable.data = list;
                this.mainTable.sumData = sumData;

                // 空数据提示
                if (list.length === 0) {
                    this.$Message.info("当前查询条件下暂无数据");
                }

            }
            else {
                const errorMessage = response.message || '未知错误';
                console.error("API返回错误:", response);
                this.$Message.error(`获取数据失败: ${errorMessage}`);

                // 根据错误类型提供不同提示
                if (errorMessage.includes('权限') || errorMessage.includes('认证')) {
                    this.$Message.warning("请检查您的登录状态");
                }
                else if (errorMessage.includes('网络') || errorMessage.includes('连接')) {
                    this.$Message.warning("网络连接不稳定，请稍后重试");
                }
            }
        },

        /**
         * 处理数据加载错误
         */
        handleDataError(error) {
            console.error("获取统计数据失败:", error);

            // 网络错误处理
            if (error.message.includes('Network') || error.message.includes('timeout')) {
                this.$Message.error("网络连接失败，请检查网络后重试");
            }
            // HTTP状态码错误
            else if (error.response) {
                const status = error.response.status;
                if (status === 401) {
                    this.$Message.warning("登录已过期，请重新登录");
                }
                else if (status === 403) {
                    this.$Message.warning("权限不足，无法访问该数据");
                }
                else if (status >= 500) {
                    this.$Message.error("服务器内部错误，请稍后重试");
                }
                else {
                    this.$Message.error(`请求失败 (${status})`);
                }
            }
            // 其他错误
            else {
                this.$Message.error("数据处理失败，请稍后重试");
            }
        },

        // ==================== 导出相关 ====================

        /**
         * 执行导出操作
         */
        async performExport() {
            try {
                // 克隆搜索配置并设置导出参数
                const exportSearch = this.prepareExportSearch();

                // 创建导出下载链接
                const downloadUrl = this.buildExportUrl(exportSearch);
                const fileName = this.generateExportFileName();

                // 执行文件下载
                await this.downloadFile(downloadUrl, fileName);

                this.$Message.success("导出成功");
            }
            catch (error) {
                throw error; // 重新抛出，由外层统一处理
            }
        }


    }
};
</script>

<style scoped>
/* ==================== 工具栏样式 ==================== */

.tool-bar-row {
    margin-bottom: 16px;
}

.global-tool-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #fafafa;
    border-radius: 6px;
    border: 1px solid #e8eaec;
}

.left-bar {
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1;
}

.left-bar .title {
    font-size: 16px;
    font-weight: 600;
    color: #2d8cf0;
    white-space: nowrap;
}

.filter-controls {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.filter-label {
    color: #666;
    font-size: 14px;
    white-space: nowrap;
}

.date-picker {
    min-width: 200px;
}

/* 单选框组样式 */
.filter-controls .ivu-radio-group {
    display: flex;
    align-items: center;
}

.filter-controls .ivu-radio-wrapper {
    margin-right: 16px;
    font-size: 14px;
}

.filter-controls .ivu-radio-wrapper:last-child {
    margin-right: 0;
}

.filter-controls .ivu-radio-wrapper-checked {
    color: #2d8cf0;
    font-weight: 500;
}

.right-bar {
    display: flex;
    gap: 12px;
}

.action-btn {
    min-width: 80px;
    font-weight: 500;
}

/* ==================== 表格单元格样式 ==================== */

.money-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 12px;
    min-height: 40px;
}

.money-text {
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-weight: 600;
    font-size: 13px;
    color: #e74c3c;
}

.money-text.money-negative {
    color: #ff6b6b;
}

.money-text.money-positive {
    color: #51cf66;
}

.action-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
}

.action-cell .ivu-btn-text {
    color: #2d8cf0;
    transition: all 0.3s ease;
}

.action-cell .ivu-btn-text:hover {
    color: #57a3f3;
    background-color: #f0f9ff;
}

/* ==================== 响应式设计 ==================== */

@media (max-width: 1200px) {
    .global-tool-bar {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
        padding: 16px;
    }

    .left-bar {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    .filter-controls {
        width: 100%;
        justify-content: flex-start;
    }

    .right-bar {
        justify-content: center;
    }
}

@media (max-width: 768px) {
    .filter-controls {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .filter-controls .ivu-radio-group {
        margin-bottom: 8px;
    }

    .date-picker {
        width: 100%;
        min-width: unset;
    }

    .action-btn {
        flex: 1;
        min-width: unset;
    }
}

/* ==================== 过渡动画 ==================== */

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* ==================== 加载状态样式 ==================== */

.ivu-spin-fix {
    z-index: 1000;
    background-color: rgba(255, 255, 255, 0.8);
}

/* ==================== 表格增强样式 ==================== */

/* 金额列特殊样式 */
.ivu-table td.money-column {
    background-color: #fafafa;
    font-weight: 500;
}

.ivu-table .ivu-table-row:hover td.money-column {
    background-color: #f0f9ff;
}

/* 固定列阴影 */
.ivu-table-fixed-right {
    box-shadow: -2px 0 6px rgba(0, 0, 0, 0.1);
}

/* 空状态样式 */
.ivu-table-empty {
    padding: 40px 0;
}

.ivu-table-empty .ivu-table-empty-text {
    color: #999;
    font-size: 14px;
}

/* ==================== 公式图例样式 ==================== */

.formula-legend-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    /* 小屏幕自动换行 */
    background-color: #f8f8f9;
    /* 浅灰背景，与卡片区分 */
    border: 1px dashed #dcdee2;
    /* 虚线边框，表示这是说明性内容 */
    border-radius: 4px;
    padding: 10px 16px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #515a6e;
}

.legend-item {
    display: flex;
    align-items: center;
    line-height: 1.5;
}

.legend-item .tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    font-weight: 600;
    font-size: 12px;
    margin-right: 8px;
}

/* 收入样式 - 绿色系 */
.legend-item .tag.income {
    background-color: #e8f9f0;
    color: #19be6b;
    border: 1px solid #a3e6c6;
}

/* 支出样式 - 红色系 */
.legend-item .tag.expense {
    background-color: #fff1f0;
    color: #ed4014;
    border: 1px solid #ffccc7;
}

.legend-item .equals {
    margin-right: 8px;
    color: #808695;
    font-weight: bold;
}

.legend-item .formula-text {
    color: #515a6e;
}

/* 垂直分割线 */
.vertical-divider {
    width: 1px;
    height: 14px;
    background-color: #dcdee2;
    margin: 0 24px;
}

/* 响应式调整：屏幕较窄时隐藏分割线并调整间距 */
@media (max-width: 992px) {
    .formula-legend-container {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .vertical-divider {
        display: none;
    }
}
</style>
